// Import Scapple Board… (the project menu, and the Mind Maps tab's map menu): a `.scap` chosen, read in the browser
// (core `scappleImport`) into a new map in Research & Notes ▸ Mind Maps, then shown on Plan ▸ Mind Maps, where the canvas
// says what came in and offers Link Notes to Cast…. A file that isn't a board is said so in a notice.
import { useState, type ReactNode } from 'react';
import { announce } from '../../shell/context.ts';
import { navigate } from '../../shell/route.ts';
import { importScappleBoard } from '../../store/maps.ts';
import type { LiveProject } from '../../store/project.ts';
import { ConfirmDialog } from '../Menu.tsx';

/** Asks for a board (a `.scap`): the file chosen, or null. The input is made for the choice, hidden, and taken away. */
function chooseBoard(): Promise<File | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.scap';
    input.hidden = true;
    input.dataset.scappleInput = '';
    const done = (file: File | null) => {
      input.remove();
      resolve(file);
    };
    input.addEventListener('change', () => done(input.files?.[0] ?? null), { once: true });
    input.addEventListener('cancel', () => done(null), { once: true });
    document.body.appendChild(input);
    input.click();
  });
}

/** Import Scapple Board…: `start` asks for the file and imports it; `element` is the notice when it couldn't be read. */
export function useScappleImport(o: { live: LiveProject | null; projectId: string; mapsHref: string }): { start: () => void; element: ReactNode } {
  const [problem, setProblem] = useState<string | null>(null);
  const start = () => {
    const live = o.live;
    if (!live) return;
    void (async () => {
      const file = await chooseBoard();
      if (!file) return;
      try {
        const bytes = new Uint8Array(await file.arrayBuffer());
        const made = importScappleBoard(live, o.projectId, file.name, bytes);
        announce(`Imported “${file.name}”: ${made.summary}`);
        navigate(o.mapsHref);
      } catch (err) {
        const text = err instanceof Error ? err.message : String(err);
        announce(text);
        setProblem(text);
      }
    })();
  };
  const element = problem ? (
    <ConfirmDialog title="Import Scapple Board" message={<p>{problem}</p>} confirm="OK" cancel={null} onConfirm={() => setProblem(null)} onCancel={() => setProblem(null)} />
  ) : null;
  return { start, element };
}
